import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Radio,
    TextField,
    InputAdornment,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import { WB_SX } from "../../../ui/ux/uiTokens";

const DEBOUNCE_MS = 400;

const TemplateSelectionStep = ({ onTemplateSelect, selectedTemplate }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchTemplates();
    }, [page, rowsPerPage, debouncedSearchTerm]);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                is_active: true,
                status: "APPROVED",
                page: String(page + 1),
                per_page: String(rowsPerPage),
            });
            const normalizedSearch = debouncedSearchTerm.trim().replace(/\s+/g, " ");
            if (normalizedSearch) {
                queryParams.set("template_name", normalizedSearch);
            }
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/templates/db/list?${queryParams.toString()}`
            );

            if (response.data?.success && response.data?.data) {
                setTemplates(response.data.data);
                setTotal(response.data.pagination?.total || 0);
            } else {
                setTemplates([]);
                setTotal(0);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
            setTemplates([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = (template) => {
        onTemplateSelect(template);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const isSelected = (template) => {
        return (
            selectedTemplate &&
            (selectedTemplate.id === template.id ||
                selectedTemplate.template_id === template.template_id)
        );
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                    Select Template
                </Typography>
                <TextField
                    placeholder="Search by template name"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: "action.active" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper elevation={0} sx={{ ...WB_SX.tableContainer, maxWidth: "100%" }}>
                    <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 480 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" sx={WB_SX.tableHeadCell} />
                                    <TableCell sx={WB_SX.tableHeadCell}>
                                        Template Name
                                    </TableCell>
                                    <TableCell sx={WB_SX.tableHeadCell}>
                                        Category
                                    </TableCell>
                                    <TableCell sx={WB_SX.tableHeadCell}>
                                        Language
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templates.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                            sx={{ py: 4 }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                No approved templates found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    templates.map((template) => {
                                        const selected = isSelected(template);
                                        return (
                                            <TableRow
                                                key={template.id}
                                                hover
                                                onClick={() =>
                                                    handleTemplateSelect(template)
                                                }
                                                sx={{
                                                    cursor: "pointer",
                                                    backgroundColor: selected
                                                        ? "action.selected"
                                                        : "inherit",
                                                    "&:hover": {
                                                        backgroundColor:
                                                            "action.hover",
                                                    },
                                                    ...WB_SX.tableRow,
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Radio
                                                        checked={selected}
                                                        onChange={() =>
                                                            handleTemplateSelect(
                                                                template
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {template.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={template.category}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {template.language}
                                                    </Typography>
                                                </TableCell>
                                                {/* <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: "monospace",
                                                            fontSize: "0.875rem",
                                                        }}
                                                    >
                                                        {template.template_id}
                                                    </Typography>
                                                </TableCell> */}
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {total > 0 && (
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    )}
                </Paper>
            )}

            {selectedTemplate && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Selected: {selectedTemplate.name} ({selectedTemplate.language || "N/A"})
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default TemplateSelectionStep;

